import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Button,
    Grid,
    TextField,
    Typography,
    Box,
    Card,
    CardHeader,
    Dialog,
    DialogTitle,
    DialogActions,
    Tooltip,
    IconButton,
    DialogContent,
    CardContent,
    CircularProgress,
    MenuItem,
    useTheme,
    useMediaQuery,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { toast } from "react-toastify";
import { Add, Close, Delete, Edit, FileDownload, PictureAsPdf, Print, Visibility } from "@mui/icons-material";
import autoTable from "jspdf-autotable";
import jsPDF from "jspdf";
import { PermissionService } from "../../../config/permissionService";
import { getSelectedExportRows } from "../../../config/exportSelection";
import { createShippingCharge, deleteShippingCharge, getAllShippingCharge, updateShippingCharge } from "../../State/ShippingCharge/Action";
import countries from '../../../config/country-currency-code.json';

const AddShippingCharge = () => {
    const { shippingCharges } = useSelector((store) => store);
    const [editingId, setEditingId] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null });
    const [openFormModal, setOpenFormModal] = useState(false);
    const [searchText, setSearchText] = useState("");

  const [selectedRowIds, setSelectedRowIds] = useState([]);
    const dispatch = useDispatch();
    const [openView, setOpenView] = useState(false);
    const [selectedCurrency, setSelectedCurrency] = useState(null);
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm")); // mobile पर fullscreen

    useEffect(() => {
        dispatch(getAllShippingCharge());
    }, [dispatch]);

    const [formData, setFormData] = useState({
        country: "",
        currencyCode: "",
        rangeCharges: [{ id: null, fromAmount: "", toAmount: "", percentage: "" }],
    });

    // handle country change and set currencyCode automatically
    const handleCountryChange = (e) => {
        const country = e.target.value;
        const sel = countries.find(c => c.country === country);
        setFormData(prev => ({ ...prev, country: country, currencyCode: sel ? sel.currency[0] : "" }));
    };

    const handleRangeChange = (index, field, value) => {
        setFormData(prev => {
            const ranges = [...prev.rangeCharges];
            ranges[index] = { ...ranges[index], [field]: value };

            // if last row and all three fields are non-empty (trim) => append new empty row
            const lastIndex = ranges.length - 1;
            const last = ranges[lastIndex];
            if (
                last &&
                last.fromAmount.toString().trim() !== "" &&
                last.toAmount.toString().trim() !== "" &&
                last.percentage.toString().trim() !== ""
            ) {
                ranges.push({ id: null, fromAmount: "", toAmount: "", percentage: "" });
            }

            return { ...prev, rangeCharges: ranges };
        });
    };

    const handleRemoveRow = (index) => {
        setFormData(prev => {
            const ranges = prev.rangeCharges.filter((_, i) => i !== index);
            // Ensure at least one empty row remains
            if (ranges.length === 0) {
                ranges.push({ id: null, fromAmount: "", toAmount: "", percentage: "" });
            }
            return { ...prev, rangeCharges: ranges };
        });
    };

    const buildPayload = () => {
        // Only include fully filled rows
        const filtered = formData.rangeCharges
            .filter(r => r.fromAmount !== "" && r.toAmount !== "" && r.percentage !== "")
            .map(r => ({
                id: r.id || null,
                fromAmount: Number(r.fromAmount),
                toAmount: Number(r.toAmount),
                percentage: Number(r.percentage)
            }));

        return {
            country: formData.country,
            currencyCode: formData.currencyCode,
            rangeCharges: filtered
        };
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (formData.country === "") {
            toast.error("Please select country !!.");
            return;
        }

        let isValid = true;
        let isFilled = true;

        formData.rangeCharges.forEach((rc, idx) => {
            if (rc.fromAmount !== "" && rc.toAmount !== "") {
                if (Number(rc.toAmount) <= Number(rc.fromAmount)) {
                    isValid = false;
                }
                if (rc.percentage === "") {
                    isFilled = false;
                }
            }
        });

        if (!isValid) {
            toast.error("To Amount must be greater than From Amount!");
            return;
        }
        if (!isFilled) {
            toast.error("Percentage are required!");
            return;
        }

        const payload = buildPayload();
        if (editingId) {
            dispatch(updateShippingCharge(editingId, payload, handleCancle));
        } else {
            dispatch(createShippingCharge(payload, handleCancle));
        }

    };

    const handleShippingChargeDelete = (id) => {
        setDeleteConfirm({ open: true, id });
    };

    const confirmDelete = () => {
        dispatch(deleteShippingCharge(deleteConfirm.id));
        setDeleteConfirm({ open: false, id: null });
    };

    const handleShippingChargeUpdate = (shippingCharge) => {
        // map DB ranges into form rows and add an extra blank row
        const rows = (shippingCharge.rangeCharges || []).map(r => ({
            id: r.id,
            fromAmount: r.fromAmount || r.fromAmount === 0 ? r.fromAmount : "",
            toAmount: r.toAmount || r.toAmount === 0 ? r.toAmount : "",
            percentage: r.percentage || r.percentage === 0 ? r.percentage : ""
        }));
        // ensure trailing empty row to allow new add
        rows.push({ id: null, fromAmount: "", toAmount: "", percentage: "" });

        setFormData({
            country: shippingCharge.country,
            currencyCode: shippingCharge.currencyCode,
            rangeCharges: rows
        });
        setEditingId(shippingCharge.id);
        setOpenFormModal(true);
    };

    const handleCancle = () => {
        setFormData({
            country: "",
            currencyCode: "",
            rangeCharges: [{ id: null, fromAmount: "", toAmount: "", percentage: "" }],
            deletedIds: [],
        });
        setEditingId(null);
        setOpenFormModal(false);
    }

    const handleViewProduct = (row) => {
        setSelectedCurrency(row); // पूरा row (country, currencyCode, rangeCharges)
        setOpenView(true);
    };

    const handleCloseView = () => {
        setOpenView(false);
        setSelectedCurrency(null);
    };

    const readAction = PermissionService.has({ category: "Admin", page: "Shipping Charge", type: "READ" });
    const writeAction = PermissionService.has({ category: "Admin", page: "Shipping Charge", type: "WRITE" });

    const columns = [
        ...(readAction ? [
            {
                field: "serialNo",
                headerName: "S.No.",
                width: 150,
                sortable: false,
                filterable: false,
                renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1
            },
            { field: "country", headerName: "Country", flex: 1 },
            { field: "currencyCode", headerName: "Currency Code", flex: 1 },
            {
                field: "fullProduct",
                headerName: "View Charges",
                width: 120,
                renderCell: (params) => (
                    <IconButton
                        size="small"
                        color="primary"
                        sx={{ p: 0, m: 0, cursor: "pointer" }}
                        onClick={() => handleViewProduct(params.row)}
                    >
                        <Visibility fontSize="small" sx={{ fontSize: 18, cursor: "pointer" }} />
                    </IconButton>
                ),
            },
        ] : []),

        ...(writeAction ? [
            {
                field: "actions",
                headerName: "Action",
                width: 120,
                sortable: false,
                filterable: false,
                renderCell: (params) => (
                    <Box display="flex" gap={0} sx={{ p: 0, m: 0, minHeight: 0 }}>
                        <Tooltip title="Edit">
                            <IconButton
                                size="small"
                                sx={{ p: 0, m: 0, minWidth: 24, minHeight: 24, cursor: "pointer" }}
                                onClick={() => handleShippingChargeUpdate(params.row)}
                            >
                                <Edit fontSize="small" sx={{ fontSize: 15, cursor: "pointer" }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                            <IconButton
                                color="error"
                                size="small"
                                sx={{ p: 0, m: 0, minWidth: 24, minHeight: 24, cursor: "pointer" }}
                                onClick={() => handleShippingChargeDelete(params.row.id)}
                            >
                                <Delete fontSize="small" sx={{ fontSize: 15, cursor: "pointer" }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )
            },
        ] : []),
    ];


    const filteredRows = shippingCharges?.shippingCharges?.response?.filter(row => {
        const query = searchText.toLowerCase();
        return (
            row.country?.toLowerCase().includes(query.toLowerCase()),
            row.currencyCode?.toLowerCase().includes(query.toLowerCase())
        );
    });

      const getExportData = () => getSelectedExportRows(filteredRows, selectedRowIds);

  const handleExportCSV = () => {
    const exportRows = getExportData();
    if (!exportRows) return;
        const csvContent = exportRows.map((row, index) => [index + 1, row.country, row.currencyCode, row.shippingCharge].join(',')).join('\n');
        const headers = ['S.No.', 'Country', 'Currency Code', 'Shipping Charge'].join(',');
        const csv = `${headers}\n${csvContent}`;
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'shippingCharges.csv';
        link.click();
    };
    const handleExportPDF = () => {
      const exportRows = getExportData();
      if (!exportRows) return;
        const doc = new jsPDF();
        doc.setFontSize(12);
        doc.text("ShippingCharge List", 14, 15);
        const tableColumn = ['S.No.', 'Country', 'Currency Code', 'Shipping Charge'];
        const tableRows = exportRows.map((row, index) => [index + 1, row.country, row.currencyCode, row.shippingCharge]);
        autoTable(doc, {
            head: [tableColumn],
            body: tableRows,
            startY: 20,
            theme: 'striped',
            headStyles: {
                fillColor: [37, 99, 235], // Tailwind blue-600 (RGB: 37,99,235)
                textColor: [255, 255, 255], // white text
                halign: 'left',
            },
            styles: { fontSize: 8, cellPadding: 1, overflow: 'linebreak' },
        });
        doc.save('shippingCharges.pdf');
    };
    const handlePrint = () => {
      const exportRows = getExportData();
      if (!exportRows) return;
        const printWindow = window.open('', '_blank');
        const headers = ['S.No.', 'Country', 'Currency Code', 'Shipping Charge'];
        const tableContent = exportRows.map((row, index) => [index + 1, row.country, row.currencyCode, row.shippingCharge]);
        const html = `
                  <html>
                    <head>
                      <title>ShippingCharge List</title>
                      <style>
                        table { border-collapse: collapse; width: 100%; }
                        th, td { border: 1px solid black; padding: 8px; text-align: left; }
                        th {background: #449AEF;
                color: white;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact; }
                        tr:nth-child(even) { background-color: #f5f5f5; }
                      </style>
                    </head>
                    <body>
                      <h2>ShippingCharge List</h2>
                      <table>
                        <thead>
                          <tr>
                            ${headers.map(header => `<th>${header}</th>`).join('')}
                          </tr>
                        </thead>
                        <tbody>
                          ${tableContent.map(row => `
                            <tr>
                              ${row.map(cell => `<td>${cell}</td>`).join('')}
                            </tr>
                          `).join('')}
                        </tbody>
                      </table>
                    </body>
                  </html>
                `;
        printWindow.document.write(html);
        printWindow.document.close();
        printWindow.print();
    };

    return (
        <div >
            <Card sx={{ p: 2, borderRadius: 2, boxShadow: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <CardHeader
                        title={<Typography variant="h6" className="text-gray-700">Additional Charges</Typography>}
                        sx={{ p: 0 }}
                    />
                    {writeAction && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <IconButton
                                sx={{
                                    background: "linear-gradient(90deg, #449AEF 0%, #f87171 100%)",
                                    color: "#fff",
                                    boxShadow: "0 2px 8px rgba(239,68,68,0.15)",
                                    transition: "all 0.2s",
                                    "&:hover": {
                                        background: "linear-gradient(90deg, #f87171 0%, #449AEF 100%)",
                                        boxShadow: "0 4px 16px rgba(239,68,68,0.25)",
                                        transform: "scale(1.02)",
                                    },
                                    cursor: "pointer",
                                    p: 1,
                                    borderRadius: '50%',
                                }}
                                onClick={() => {
                                    setOpenFormModal(true);
                                    setEditingId(null);
                                }}
                            >
                                <Add />
                            </IconButton>
                            <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: 700, color: "#449AEF", fontSize: "1.2rem", letterSpacing: 0.2 }}
                            >
                                Additional Charge
                            </Typography>
                        </Box>
                    )}
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
                    <Box sx={{ display: 'flex', gap: 1.5, mb: { xs: 2, sm: 0 } }}>
                        <Button
                            variant="contained"
                            startIcon={<FileDownload />}
                            onClick={handleExportCSV}
                            sx={{
                                background: 'linear-gradient(90deg, #592545 0%, #a63f7b 100%)',
                                color: '#fff',
                                fontWeight: 600,
                                fontSize: '0.85rem',
                                borderRadius: '6px',
                                px: 2,
                                py: 0.8,
                                transition: 'all 0.2s',
                                '&:hover': {
                                    background: 'linear-gradient(90deg, #a63f7b 0%, #592545 100%)',
                                    boxShadow: '0 4px 12px rgba(89,37,69,0.2)',
                                    transform: 'scale(1.02)',
                                },
                                cursor: 'pointer',
                                width: { xs: '100%', sm: 'auto' },
                            }}
                        >
                            Export CSV
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<PictureAsPdf />}
                            onClick={handleExportPDF}
                            sx={{
                                background: 'linear-gradient(90deg, #592545 0%, #a63f7b 100%)',
                                color: '#fff',
                                fontWeight: 600,
                                fontSize: '0.85rem',
                                borderRadius: '6px',
                                px: 2,
                                py: 0.8,
                                transition: 'all 0.2s',
                                '&:hover': {
                                    background: 'linear-gradient(90deg, #a63f7b 0%, #592545 100%)',
                                    boxShadow: '0 4px 12px rgba(89,37,69,0.2)',
                                    transform: 'scale(1.02)',
                                },
                                cursor: 'pointer',
                                width: { xs: '100%', sm: 'auto' },
                            }}
                        >
                            Export PDF
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<Print />}
                            onClick={handlePrint}
                            sx={{
                                background: 'linear-gradient(90deg, #592545 0%, #a63f7b 100%)',
                                color: '#fff',
                                fontWeight: 600,
                                fontSize: '0.85rem',
                                borderRadius: '6px',
                                px: 2,
                                py: 0.8,
                                transition: 'all 0.2s',
                                '&:hover': {
                                    background: 'linear-gradient(90deg, #a63f7b 0%, #592545 100%)',
                                    boxShadow: '0 4px 12px rgba(89,37,69,0.2)',
                                    transform: 'scale(1.02)',
                                },
                                cursor: 'pointer',
                                width: { xs: '100%', sm: 'auto' },
                            }}
                        >
                            Print
                        </Button>
                    </Box>
                    <Box>
                        <TextField
                            label="Search Shipping Charges"
                            variant="outlined"
                            size="small"
                            value={searchText}
                            onChange={e => setSearchText(e.target.value)}
                            sx={{ width: { xs: '100%', sm: '300px' } }}
                        />
                    </Box>
                </Box>
                {/* DataGrid */}
                <Box sx={{ height: "100%", width: "100%" }}>
                    <DataGrid
                        rows={filteredRows}
                        columns={columns}
                        pagination
                        initialState={{
                            pagination: { paginationModel: { pageSize: 10, page: 0 } },
                        }}
                        pageSizeOptions={[10, 25, 50, 75, 100]}
                        getRowId={(row) => row.id}
                                    checkboxSelection
                        onRowSelectionModelChange={(ids) => setSelectedRowIds(ids)}
                        rowSelectionModel={selectedRowIds}
                        disableSelectionOnClick
                        density="compact"
                        experimentalFeatures={{ virtualization: true }}
                        loading={shippingCharges?.loading}
                        autoHeight={true}
                        sx={{
                            borderRadius: 2,
                            fontSize: "0.8rem",
                            "& .MuiDataGrid-cell": {
                                fontSize: "0.8rem",
                                border: "1px solid rgb(204, 201, 201)",
                                minHeight: "36px !important",
                                height: "36px !important",
                                lineHeight: "1.2",
                                paddingTop: "4px",
                                paddingBottom: "4px",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            },
                            "& .MuiDataGrid-columnHeader": {
                                backgroundColor: "#449AEF",
                                color: "#fff",
                                fontWeight: "bold",
                                fontSize: "0.8rem",
                                padding: "8px",
                                border: "1px solid rgb(204, 201, 201)",
                                minHeight: "36px !important",
                                height: "36px !important",
                                lineHeight: "1.2",
                            },
                            "& .MuiDataGrid-columnHeaderTitle": {
                                fontSize: "0.8rem",
                                fontWeight: 600,
                            },
                            "& .MuiDataGrid-row": {
                                maxHeight: "36px !important",
                                minHeight: "36px !important",
                            },
                            "& .MuiDataGrid-row:nth-of-type(odd)": {
                                backgroundColor: "#f5f5f5",
                            },
                            "& .MuiDataGrid-row:nth-of-type(even)": {
                                backgroundColor: "#ffffff",
                            },
                            "& .MuiDataGrid-row:hover": {
                                backgroundColor: "#f5f5f5",
                            },
                        }}
                    />
                </Box>
            </Card>

            <Dialog open={deleteConfirm.open} onClose={() => setDeleteConfirm({ open: false, id: null })}>
                <DialogTitle>Are you sure you want to delete this Country With Additional Charges?</DialogTitle>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirm({ open: false, id: null })}>Cancel</Button>
                    <Button
                        onClick={confirmDelete}
                        sx={{ color: "#fff", backgroundColor: "#449AEF", "&:hover": { backgroundColor: "#dc2626" } }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Replace the form rendering with a Dialog: */}
            <Dialog
                open={openFormModal}
                onClose={handleCancle}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: {
                        borderRadius: '12px',
                        boxShadow: '0 8px 30px rgba(0,0,0,0.1)',
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        fontSize: '1.25rem',
                        fontWeight: 'bold',
                        mt: 1,
                        mb: 0.5,
                        pb: 1,
                        borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
                    }}
                >
                    Additional Charge
                    <IconButton onClick={handleCancle} size="small" sx={{ ml: 2 }}>
                        <Close />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ px: 2, py: 1 }}>
                    <Card className="mb-4 mt-4" sx={{ borderRadius: 2, boxShadow: 1 }}>
                        <CardContent sx={{ p: 2 }}>
                            <form onSubmit={handleSubmit}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            select
                                            fullWidth
                                            required
                                            size="small"
                                            disabled={editingId}
                                            label="Country"
                                            name="country"
                                            value={formData.country}
                                            onChange={handleCountryChange}
                                            variant="outlined"
                                        >
                                            {countries.map((option) => (
                                                <MenuItem key={option.country} value={option.country}>
                                                    {option.country}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>

                                    {/* Currency Code (Auto from Country) */}
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Currency Code"
                                            name="currencyCode"
                                            value={formData.currencyCode}
                                            disabled
                                            variant="outlined"
                                            size="small"
                                        />
                                    </Grid>

                                    {/* Range rows */}
                                    {formData.rangeCharges.map((r, idx) => (
                                        <Grid item xs={12} key={r.id ?? `new-${idx}`} container spacing={1} alignItems="center">
                                            <Grid item xs={12} md={3.6}>
                                                <TextField
                                                    label="From Amount"
                                                    value={r.fromAmount}
                                                    onChange={(e) => {
                                                        const val = e.target.value;
                                                        if (/^\d*$/.test(val)) {  // सिर्फ digits allow
                                                            handleRangeChange(idx, "fromAmount", val);
                                                        }
                                                    }}
                                                    fullWidth
                                                    variant="outlined"
                                                    size="small"
                                                    inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={3.6}>
                                                <TextField
                                                    label="To Amount"
                                                    value={r.toAmount}
                                                    onChange={(e) => {
                                                        const val = e.target.value;
                                                        if (/^\d*$/.test(val)) {  // सिर्फ digits allow
                                                            handleRangeChange(idx, "toAmount", val);
                                                        }
                                                    }}
                                                    fullWidth
                                                    variant="outlined"
                                                    size="small"
                                                    error={
                                                        r.fromAmount !== "" &&
                                                        r.toAmount !== "" &&
                                                        Number(r.toAmount) <= Number(r.fromAmount)
                                                    }
                                                    inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={3.6}>
                                                <TextField
                                                    label="Charge (%)"
                                                    value={r.percentage}
                                                    onChange={(e) => {
                                                        const val = e.target.value;
                                                        if (/^\d{0,2}$/.test(val)) {  // सिर्फ digits और max 2 digit
                                                            handleRangeChange(idx, "percentage", val);
                                                        }
                                                    }}
                                                    fullWidth
                                                    variant="outlined"
                                                    size="small"
                                                    inputProps={{ inputMode: "numeric", pattern: "[0-9]*", maxLength: 2 }}
                                                />
                                            </Grid>


                                            <Grid item xs={12} md={1}>
                                                {formData.rangeCharges.length > 1 && (
                                                    <IconButton onClick={() => handleRemoveRow(idx)}>
                                                        <Delete color="error" />
                                                    </IconButton>
                                                )}
                                            </Grid>

                                        </Grid>
                                    ))}


                                    <Grid item xs={12} sx={{ display: "flex", justifyContent: "center", gap: 2, }}>
                                        <Button
                                            variant="contained"
                                            type="submit"
                                            disabled={shippingCharges.loading}
                                            sx={{
                                                background: "linear-gradient(90deg, #449AEF 0%, #f87171 100%)",
                                                color: "#fff",
                                                fontWeight: 600,
                                                fontSize: "0.85rem",
                                                borderRadius: "6px",
                                                boxShadow: "0 2px 8px rgba(239,68,68,0.15)",
                                                px: 2.5,
                                                py: 0.8,
                                                transition: "all 0.2s",
                                                "&:hover": {
                                                    background: "linear-gradient(90deg, #f87171 0%, #449AEF 100%)",
                                                    boxShadow: "0 4px 16px rgba(239,68,68,0.25)",
                                                    transform: "scale(1.02)",
                                                },
                                                width: { xs: "100%", sm: "auto" },
                                                textAlign: "center"
                                            }}
                                        >
                                            {shippingCharges.loading ? (
                                                <>
                                                    <CircularProgress size={20} sx={{ mr: 1 }} />
                                                    Processing...
                                                </>
                                            ) : (editingId ? "Update" : "Add")}
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            onClick={handleCancle}
                                            sx={{
                                                color: "#449AEF",
                                                borderColor: "#449AEF",
                                                fontWeight: 600,
                                                fontSize: "0.85rem",
                                                borderRadius: "6px",
                                                px: 2.5,
                                                py: 0.8,
                                                transition: "all 0.2s",
                                                "&:hover": {
                                                    backgroundColor: "#449AEF",
                                                    color: "#fff",
                                                    borderColor: "#449AEF",
                                                    boxShadow: "0 2px 8px rgba(239,68,68,0.1)",
                                                },
                                                width: { xs: "100%", sm: "auto" },
                                                textAlign: "center"
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                    </Grid>

                                </Grid>
                            </form>
                        </CardContent>
                    </Card>
                </DialogContent>
            </Dialog>

            {/* view dialog popup */}

            <Dialog
                open={openView}
                onClose={handleCloseView}
                fullScreen={fullScreen}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle
                    sx={{
                        fontWeight: 600,
                        fontSize: { xs: "1rem", sm: "1.25rem" },
                        textAlign: "center",
                    }}
                >
                    Additional Charges Details
                </DialogTitle>

                <DialogContent dividers>
                    {selectedCurrency && (
                        <Box>
                            {/* Country & Currency Code */}
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Country"
                                        value={selectedCurrency.country}
                                        disabled
                                        variant="outlined"
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Currency Code"
                                        value={selectedCurrency.currencyCode}
                                        disabled
                                        variant="outlined"
                                        size="small"
                                    />
                                </Grid>
                            </Grid>

                            {/* Range Charges Table */}
                            <Typography
                                variant="h6"
                                gutterBottom
                                sx={{ mt: 1, fontSize: { xs: "1rem", sm: "1.15rem" }, fontWeight: 600 }}
                            >
                                Range Charges
                            </Typography>

                            {/* Mobile → Card view, Desktop → Table */}
                            {selectedCurrency.rangeCharges && selectedCurrency.rangeCharges.length > 0 ? (
                                <>
                                    {/* Desktop/Table View */}
                                    <Box sx={{ display: { xs: "none", sm: "block" }, overflowX: "auto" }}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell><strong>From Amount</strong></TableCell>
                                                    <TableCell><strong>To Amount</strong></TableCell>
                                                    <TableCell><strong>Charge (%)</strong></TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {selectedCurrency.rangeCharges.map((rc, index) => (
                                                    <TableRow key={index}>
                                                        <TableCell>{rc.fromAmount}</TableCell>
                                                        <TableCell>{rc.toAmount}</TableCell>
                                                        <TableCell>{rc.percentage}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </Box>

                                    {/* Mobile/Card View */}
                                    <Box sx={{ display: { xs: "block", sm: "none" } }}>
                                        {selectedCurrency.rangeCharges.map((rc, index) => (
                                            <Box
                                                key={index}
                                                sx={{
                                                    border: "1px solid #e0e0e0",
                                                    borderRadius: "8px",
                                                    p: 2,
                                                    mb: 2,
                                                    boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
                                                }}
                                            >
                                                <Typography variant="body2">
                                                    <strong>From:</strong> {rc.fromAmount}
                                                </Typography>
                                                <Typography variant="body2">
                                                    <strong>To:</strong> {rc.toAmount}
                                                </Typography>
                                                <Typography variant="body2">
                                                    <strong>Charge (%):</strong> {rc.percentage}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </>
                            ) : (
                                <Typography align="center" sx={{ mt: 2, color: "text.secondary" }}>
                                    No Range Charges Available
                                </Typography>
                            )}
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{ justifyContent: "center", p: 2 }}>
                    <Button
                        onClick={handleCloseView}
                        variant="contained"
                        sx={{
                            background: "linear-gradient(90deg, #449AEF 0%, #f87171 100%)",
                            fontWeight: 600,
                            px: 3,
                            py: 1,
                            "&:hover": {
                                background: "linear-gradient(90deg, #f87171 0%, #449AEF 100%)",
                            },
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

        </div>
    );
};

export default AddShippingCharge;
